/** @internal – import from `@/store/useDepartmentStore` instead. */
import { runSSEStream } from "../sseRunner";
import { buildErrorMessage } from "../utils";
import { sseEvents } from "../../../events";

export interface ContentIntelligenceInput {
  storeId: string;
  category: string;
  country: string;
  /** Index into products[] for the product the user clicked "Discover Content" on */
  selectedProductIndex: number;
  products: Array<{
    index: number;
    title: string;
    category?: string;
    adAngle?: string;
    targetKeywords?: string[];
    price?: string;
    breakevenCpa?: number;
    estimatedCpa?: Record<string, number>;
  }>;
  brandProfile: Record<string, unknown>;
  adCostEstimates: unknown[];
}

/**
 * Content-intelligence SSE pipeline runner. Posts to
 * `/core/coagent/content-intelligence` and publishes typed
 * `content-intelligence:*` events. Subscriber owns the sub-phase state
 * machine + thought stream rebuild.
 *
 * Migrated off the `SSECallbacks` mutator-bag in Phase 8 commit 78.
 */
export async function runContentIntelligenceSSE(
  input: ContentIntelligenceInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  await runSSEStream(input, {
    pipeline: "content-intelligence",
    endpoint: "/core/coagent/content-intelligence",
    sessionId,
    buildBody: (sid, inp) => ({ sessionId: sid, ...inp }),
    errorPrefix: "Content intelligence",
    signal,
    debugMeta: { storeId: input.storeId, category: input.category },
    handleEvent: (event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { phase, status, data, error, progress, details } = event as Record<string, any>;
      const ev = data as Record<string, unknown> | undefined;
      const pr = progress as Record<string, unknown> | undefined;

      if (phase === "keyword_generation" && status === "running") {
        sseEvents.publish({
          type: "content-intelligence:keyword-generation-running",
          pipeline: "content-intelligence",
          sessionId,
        });
        return;
      }

      if (phase === "keyword_generation" && status === "complete") {
        sseEvents.publish({
          type: "content-intelligence:keyword-generation-complete",
          pipeline: "content-intelligence",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "meta_discovery" && status === "running") {
        sseEvents.publish({
          type: "content-intelligence:meta-discovery-running",
          pipeline: "content-intelligence",
          sessionId,
        });
        return;
      }

      if (phase === "meta_discovery" && status === "complete") {
        sseEvents.publish({
          type: "content-intelligence:meta-discovery-complete",
          pipeline: "content-intelligence",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "tiktok_discovery" && status === "running") {
        sseEvents.publish({
          type: "content-intelligence:tiktok-discovery-running",
          pipeline: "content-intelligence",
          sessionId,
        });
        return;
      }

      if (phase === "tiktok_discovery" && status === "complete") {
        sseEvents.publish({
          type: "content-intelligence:tiktok-discovery-complete",
          pipeline: "content-intelligence",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "video_analysis" && status === "running") {
        sseEvents.publish({
          type: "content-intelligence:video-analysis-running",
          pipeline: "content-intelligence",
          sessionId,
          data: ev ?? {},
          ...(pr !== undefined ? { progress: pr } : {}),
        });
        return;
      }

      if (phase === "video_analysis" && status === "complete") {
        sseEvents.publish({
          type: "content-intelligence:video-analysis-complete",
          pipeline: "content-intelligence",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "content_synthesis" && status === "running") {
        sseEvents.publish({
          type: "content-intelligence:content-synthesis-running",
          pipeline: "content-intelligence",
          sessionId,
        });
        return;
      }

      if (phase === "content_synthesis" && status === "complete") {
        sseEvents.publish({
          type: "content-intelligence:content-synthesis-complete",
          pipeline: "content-intelligence",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "done") {
        sseEvents.publish({
          type: "content-intelligence:done",
          pipeline: "content-intelligence",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "error") {
        const message = buildErrorMessage(error, details, "Content intelligence pipeline failed");
        const failedPhase = (event as Record<string, unknown>).failedPhase;
        console.error("[coagent/content-intelligence] SSE error", { message, details });
        sseEvents.publish({
          type: "content-intelligence:error",
          pipeline: "content-intelligence",
          sessionId,
          message,
          ...(typeof failedPhase === "string" ? { failedPhase } : {}),
          ...(details !== undefined ? { details } : {}),
        });
      }
    },
  });
}
